/**
 * Case Playbooks Settings Page
 *
 * Manage case playbooks for investigation workflows.
 */

import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  ClipboardList,
  AlertTriangle,
  X,
  Copy,
  Power,
  PowerOff,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  usePlaybooks,
  useCreatePlaybook,
  useUpdatePlaybook,
  useDeletePlaybook,
} from '../../hooks/usePlaybooks';
import { useToast } from '../../contexts/ToastContext';
import {
  ASSIGNMENT_MODE_OPTIONS,
  CASE_SECTION_OPTIONS,
} from '../../config/caseConfig';

export default function CasePlaybooksSettings() {
  const toast = useToast();
  const { data: playbooks, isLoading, error } = usePlaybooks({ includeInactive: true });
  const createPlaybook = useCreatePlaybook();
  const updatePlaybook = useUpdatePlaybook();
  const deletePlaybook = useDeletePlaybook();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [expandedPlaybook, setExpandedPlaybook] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    checklist_enabled: true,
    checklist_required: true,
    checklist_items: [],
    assignment_mode: 'manual',
    is_active: true,
  });
  const [newItemLabel, setNewItemLabel] = useState('');

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 50);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      checklist_enabled: true,
      checklist_required: true,
      checklist_items: [],
      assignment_mode: 'manual',
      is_active: true,
    });
    setNewItemLabel('');
    setSelectedPlaybook(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createPlaybook.mutateAsync({
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
        checklist_items: formData.checklist_items.map((item, i) => ({ order: i, label: item, description: '' })),
        eligible_officer_ids: [],
        visible_sections: CASE_SECTION_OPTIONS.map((opt) => opt.value),
        show_ai_summary: true,
      });
      toast.success(`Playbook "${formData.name}" created`);
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      toast.error(err.message || 'Failed to create playbook');
    }
  };

  const handleEdit = (playbook) => {
    setSelectedPlaybook(playbook);
    setFormData({
      name: playbook.name,
      slug: playbook.slug,
      description: playbook.description || '',
      checklist_enabled: playbook.checklist_enabled,
      checklist_required: playbook.checklist_required,
      checklist_items: (playbook.checklist_items || []).map((item) => item.label),
      assignment_mode: playbook.assignment_mode || 'manual',
      is_active: playbook.is_active,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updatePlaybook.mutateAsync({
        id: selectedPlaybook.id,
        data: {
          name: formData.name,
          description: formData.description,
          checklist_enabled: formData.checklist_enabled,
          checklist_required: formData.checklist_required,
          checklist_items: formData.checklist_items.map((item, i) => ({ order: i, label: item, description: '' })),
          assignment_mode: formData.assignment_mode,
          is_active: formData.is_active,
        },
      });
      toast.success(`Playbook "${formData.name}" updated`);
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      toast.error(err.message || 'Failed to update playbook');
    }
  };

  const handleToggleActive = async (playbook) => {
    try {
      await updatePlaybook.mutateAsync({
        id: playbook.id,
        data: { is_active: !playbook.is_active },
      });
      toast.success(`"${playbook.name}" is now ${playbook.is_active ? 'inactive' : 'active'}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update playbook');
    }
  };

  const handleDeleteConfirm = (playbook) => {
    setSelectedPlaybook(playbook);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deletePlaybook.mutateAsync(selectedPlaybook.id);
      toast.success(`Playbook "${selectedPlaybook.name}" deleted`);
      setShowDeleteModal(false);
      resetForm();
    } catch (err) {
      toast.error(err.message || 'Failed to delete playbook');
    }
  };

  const handleDuplicate = (playbook) => {
    setFormData({
      name: `${playbook.name} (Copy)`,
      slug: `${playbook.slug}_copy`,
      description: playbook.description || '',
      checklist_enabled: playbook.checklist_enabled,
      checklist_required: playbook.checklist_required,
      checklist_items: (playbook.checklist_items || []).map((item) => item.label),
      assignment_mode: playbook.assignment_mode || 'manual',
      is_active: true,
    });
    setShowCreateModal(true);
  };

  const addChecklistItem = () => {
    if (!newItemLabel.trim()) return;
    setFormData({ ...formData, checklist_items: [...formData.checklist_items, newItemLabel] });
    setNewItemLabel('');
  };

  const removeChecklistItem = (index) => {
    setFormData({
      ...formData,
      checklist_items: formData.checklist_items.filter((_, i) => i !== index),
    });
  };

  if (error) {
    return (
      <div className="settings-error">
        <AlertTriangle size={24} />
        <p>Failed to load playbooks: {error.message}</p>
      </div>
    );
  }

  const presetPlaybooks = (playbooks || []).filter((p) => p.is_preset);
  const customPlaybooks = (playbooks || []).filter((p) => !p.is_preset);

  return (
    <div className="case-playbooks-settings">
      <style>{`
        .case-playbooks-settings {
          color: var(--text-primary);
        }
        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }
        .settings-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px;
        }
        .settings-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0;
        }
        .section-title {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 8px;
          color: var(--text-primary);
        }
        .section-hint {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 16px;
        }
        .playbook-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 32px;
        }
        .playbook-card {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 16px;
          transition: all 0.15s;
        }
        .playbook-card.inactive {
          opacity: 0.6;
        }
        .playbook-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .playbook-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }
        .playbook-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: var(--accent-glow);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent-primary);
        }
        .playbook-name {
          font-weight: 500;
          font-size: 14px;
          margin: 0 0 4px;
        }
        .playbook-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: var(--text-muted);
        }
        .playbook-badge {
          background: var(--bg-hover);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          text-transform: uppercase;
        }
        .playbook-badge.inactive {
          background: rgba(239, 68, 68, 0.15);
          color: var(--danger);
        }
        .playbook-badge.system {
          background: rgba(59, 130, 246, 0.15);
          color: var(--info);
        }
        .playbook-actions {
          display: flex;
          gap: 8px;
        }
        .action-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.15s;
        }
        .action-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }
        .action-btn.danger:hover {
          color: var(--danger);
        }
        .playbook-details {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border-color);
        }
        .checklist-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .checklist-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .checklist-item svg {
          color: var(--success);
        }
        .empty-state {
          text-align: center;
          padding: 32px;
          color: var(--text-muted);
        }
        .empty-state-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 12px;
          background: var(--bg-tertiary);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .btn-primary {
          background: var(--accent-primary);
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .btn-secondary {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 24px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-content h3 {
          margin: 0 0 20px;
          font-size: 18px;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 6px;
          color: var(--text-secondary);
        }
        .form-input, .form-select, .form-textarea {
          width: 100%;
          padding: 10px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          font-size: 14px;
          color: var(--text-primary);
        }
        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
        }
        .form-checkbox-group {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }
        .form-checkbox-group label {
          font-size: 13px;
        }
        .checklist-input-row {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }
        .checklist-input-row input {
          flex: 1;
        }
        .checklist-edit-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: var(--bg-tertiary);
          border-radius: 6px;
          margin-bottom: 8px;
          font-size: 13px;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
        }
        .btn-danger {
          background: var(--danger);
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
        }
        .warning-box {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
          display: flex;
          gap: 12px;
          color: var(--danger);
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .loading-state {
          text-align: center;
          padding: 40px;
          color: var(--text-muted);
        }
      `}</style>

      <div className="settings-header">
        <div>
          <h2 className="settings-title">Case Playbooks</h2>
          <p className="settings-subtitle">Configure investigation workflows and checklists.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={16} />
          Create Playbook
        </button>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <Loader2 size={24} className="spinner" />
          <p>Loading playbooks...</p>
        </div>
      ) : (
        <>
          <h3 className="section-title">System Playbooks</h3>
          <p className="section-hint">Built-in playbooks for common investigation types.</p>
          <div className="playbook-list">
            {presetPlaybooks.map((pb) => (
              <PlaybookCard
                key={pb.id}
                playbook={pb}
                isExpanded={expandedPlaybook === pb.id}
                onToggleExpand={() => setExpandedPlaybook(expandedPlaybook === pb.id ? null : pb.id)}
                onEdit={() => handleEdit(pb)}
                onToggleActive={() => handleToggleActive(pb)}
                onDuplicate={() => handleDuplicate(pb)}
                isPreset
              />
            ))}
            {presetPlaybooks.length === 0 && (
              <div className="empty-state">No system playbooks available.</div>
            )}
          </div>

          <h3 className="section-title">Custom Playbooks</h3>
          <p className="section-hint">Playbooks you&apos;ve created for your workflows.</p>
          <div className="playbook-list">
            {customPlaybooks.map((pb) => (
              <PlaybookCard
                key={pb.id}
                playbook={pb}
                isExpanded={expandedPlaybook === pb.id}
                onToggleExpand={() => setExpandedPlaybook(expandedPlaybook === pb.id ? null : pb.id)}
                onEdit={() => handleEdit(pb)}
                onToggleActive={() => handleToggleActive(pb)}
                onDuplicate={() => handleDuplicate(pb)}
                onDelete={() => handleDeleteConfirm(pb)}
              />
            ))}
            {customPlaybooks.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <ClipboardList size={24} />
                </div>
                <p>No custom playbooks yet</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create Playbook</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., High Risk Review"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe when this playbook should be used..."
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Assignment Mode</label>
                <select
                  className="form-select"
                  value={formData.assignment_mode}
                  onChange={(e) => setFormData({ ...formData, assignment_mode: e.target.value })}
                >
                  {ASSIGNMENT_MODE_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-checkbox-group">
                <input
                  type="checkbox"
                  id="checklist_enabled"
                  checked={formData.checklist_enabled}
                  onChange={(e) => setFormData({ ...formData, checklist_enabled: e.target.checked })}
                />
                <label htmlFor="checklist_enabled">Enable checklist</label>
              </div>
              {formData.checklist_enabled && (
                <>
                  <div className="form-checkbox-group">
                    <input
                      type="checkbox"
                      id="checklist_required"
                      checked={formData.checklist_required}
                      onChange={(e) => setFormData({ ...formData, checklist_required: e.target.checked })}
                    />
                    <label htmlFor="checklist_required">Require all items before resolution</label>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Checklist Items</label>
                    {formData.checklist_items.map((item, i) => (
                      <div key={i} className="checklist-edit-item">
                        <span>{item}</span>
                        <button type="button" className="action-btn danger" onClick={() => removeChecklistItem(i)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <div className="checklist-input-row">
                      <input
                        type="text"
                        className="form-input"
                        value={newItemLabel}
                        onChange={(e) => setNewItemLabel(e.target.value)}
                        placeholder="Add checklist item..."
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                      />
                      <button type="button" className="btn-secondary" onClick={addChecklistItem}>Add</button>
                    </div>
                  </div>
                </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={!formData.name.trim() || createPlaybook.isPending}>
                  {createPlaybook.isPending ? <><Loader2 size={16} className="spinner" /> Creating...</> : 'Create Playbook'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Playbook</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Assignment Mode</label>
                <select
                  className="form-select"
                  value={formData.assignment_mode}
                  onChange={(e) => setFormData({ ...formData, assignment_mode: e.target.value })}
                >
                  {ASSIGNMENT_MODE_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-checkbox-group">
                <input
                  type="checkbox"
                  id="edit_checklist_enabled"
                  checked={formData.checklist_enabled}
                  onChange={(e) => setFormData({ ...formData, checklist_enabled: e.target.checked })}
                />
                <label htmlFor="edit_checklist_enabled">Enable checklist</label>
              </div>
              {formData.checklist_enabled && (
                <>
                  <div className="form-checkbox-group">
                    <input
                      type="checkbox"
                      id="edit_checklist_required"
                      checked={formData.checklist_required}
                      onChange={(e) => setFormData({ ...formData, checklist_required: e.target.checked })}
                    />
                    <label htmlFor="edit_checklist_required">Require all items before resolution</label>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Checklist Items</label>
                    {formData.checklist_items.map((item, i) => (
                      <div key={i} className="checklist-edit-item">
                        <span>{item}</span>
                        <button type="button" className="action-btn danger" onClick={() => removeChecklistItem(i)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <div className="checklist-input-row">
                      <input
                        type="text"
                        className="form-input"
                        value={newItemLabel}
                        onChange={(e) => setNewItemLabel(e.target.value)}
                        placeholder="Add checklist item..."
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                      />
                      <button type="button" className="btn-secondary" onClick={addChecklistItem}>Add</button>
                    </div>
                  </div>
                </>
              )}
              <div className="form-checkbox-group">
                <input
                  type="checkbox"
                  id="edit_is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <label htmlFor="edit_is_active">Active</label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={!formData.name.trim() || updatePlaybook.isPending}>
                  {updatePlaybook.isPending ? <><Loader2 size={16} className="spinner" /> Saving...</> : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Playbook</h3>
            <div className="warning-box">
              <AlertTriangle size={20} />
              <p>Are you sure you want to delete &quot;{selectedPlaybook?.name}&quot;? This cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={handleDelete} disabled={deletePlaybook.isPending}>
                {deletePlaybook.isPending ? <><Loader2 size={16} className="spinner" /> Deleting...</> : 'Delete Playbook'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaybookCard({ playbook, isExpanded, onToggleExpand, onEdit, onToggleActive, onDuplicate, onDelete, isPreset }) {
  return (
    <div className={`playbook-card ${!playbook.is_active ? 'inactive' : ''}`}>
      <div className="playbook-header">
        <div className="playbook-info">
          <div className="playbook-icon">
            <ClipboardList size={20} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h4 className="playbook-name">{playbook.name}</h4>
              {isPreset && <span className="playbook-badge system">System</span>}
              {!playbook.is_active && <span className="playbook-badge inactive">Inactive</span>}
            </div>
            <div className="playbook-meta">
              {playbook.checklist_enabled && (
                <span><CheckCircle size={12} /> {playbook.checklist_item_count || playbook.checklist_items?.length || 0} items</span>
              )}
              <span>{ASSIGNMENT_MODE_OPTIONS.find((m) => m.value === playbook.assignment_mode)?.label || 'Manual'}</span>
            </div>
          </div>
        </div>
        <div className="playbook-actions">
          <button className="action-btn" onClick={onToggleExpand} title={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button className="action-btn" onClick={onEdit} title="Edit">
            <Pencil size={16} />
          </button>
          <button className="action-btn" onClick={onDuplicate} title="Duplicate">
            <Copy size={16} />
          </button>
          <button className="action-btn" onClick={onToggleActive} title={playbook.is_active ? 'Deactivate' : 'Activate'}>
            {playbook.is_active ? <PowerOff size={16} /> : <Power size={16} />}
          </button>
          {!isPreset && onDelete && (
            <button className="action-btn danger" onClick={onDelete} title="Delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      {isExpanded && playbook.checklist_items?.length > 0 && (
        <div className="playbook-details">
          <div className="checklist-items">
            {playbook.checklist_items.map((item, i) => (
              <div key={i} className="checklist-item">
                <CheckCircle size={14} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
